import { useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IntegrationIcon } from '@components/integrations/common';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { MarketplaceAuthIntegration } from 'api/ai-builder/requests';
import {
  ConnectModalProps,
  useConnectModal,
} from 'ui/authenticated/settings/pages/integrations/app-marketplace/connect-modal';

type MarketplaceAuthStatus = 'pending' | 'accepted' | 'rejected';

type AiBuilderMarketplaceAuthProps = {
  integrations: MarketplaceAuthIntegration[];
  status: MarketplaceAuthStatus;
  onSubmit: (results: { templateShortId: string; isConnected: boolean }[]) => Promise<void>;
  isLoading?: boolean;
};

export const AiBuilderMarketplaceAuth = ({
  integrations,
  status,
  onSubmit,
  isLoading,
}: AiBuilderMarketplaceAuthProps) => {
  const { t } = useScopedTranslate(locale);
  const isPending = status === 'pending';
  const [connectedIds, setConnectedIds] = useState<Set<string>>(
    () =>
      new Set(
        integrations
          .filter((integration) => integration.isConnected)
          .map((integration) => integration.templateShortId),
      ),
  );
  const [skippedIds, setSkippedIds] = useState<Set<string>>(
    () =>
      new Set(
        integrations
          .filter((integration) => integration.isSkipped)
          .map((integration) => integration.templateShortId),
      ),
  );
  const hasSubmittedRef = useRef(false);

  const submitResults = useCallback(
    (connected: Set<string>) => {
      if (hasSubmittedRef.current) return;
      hasSubmittedRef.current = true;
      const results = integrations.map((integration) => ({
        templateShortId: integration.templateShortId,
        isConnected: connected.has(integration.templateShortId),
      }));
      onSubmit(results).catch(() => {
        hasSubmittedRef.current = false;
      });
    },
    [integrations, onSubmit],
  );

  useEffect(() => {
    if (connectedIds.size + skippedIds.size === integrations.length) {
      submitResults(connectedIds);
    }
  }, [connectedIds, skippedIds, integrations.length, submitResults]);

  const handleConnected = useCallback((templateShortId: string) => {
    setConnectedIds((prev) => new Set(prev).add(templateShortId));
  }, []);

  const handleSkipOne = useCallback((templateShortId: string) => {
    setSkippedIds((prev) => new Set(prev).add(templateShortId));
  }, []);

  const handleSkipAll = useCallback(() => {
    setSkippedIds(
      new Set(
        integrations
          .filter((i) => !connectedIds.has(i.templateShortId))
          .map((i) => i.templateShortId),
      ),
    );
  }, [integrations, connectedIds]);

  const title =
    integrations.length === 1
      ? t('titleSingle', { name: integrations[0].name })
      : t('titleMultiple');
  const hasPendingIntegrations = connectedIds.size + skippedIds.size < integrations.length;

  return (
    <Container>
      <Text font="semibold14" text={title} color="textPrimary" />
      <Stack space={2}>
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.templateShortId}
            integration={integration}
            isPending={isPending}
            isConnected={connectedIds.has(integration.templateShortId)}
            isSkipped={skippedIds.has(integration.templateShortId)}
            onConnected={handleConnected}
            onSkip={handleSkipOne}
          />
        ))}
      </Stack>
      {integrations.length > 1 && isPending && hasPendingIntegrations && (
        <Footer>
          <Button
            variant="secondary"
            size="sm"
            text={t('skipAll')}
            onClick={handleSkipAll}
            isDisabled={!isPending || isLoading}
          />
        </Footer>
      )}
    </Container>
  );
};

type IntegrationCardProps = {
  integration: MarketplaceAuthIntegration;
  isPending: boolean;
  isConnected: boolean;
  isSkipped: boolean;
  onConnected: (templateShortId: string) => void;
  onSkip: (templateShortId: string) => void;
};

const IntegrationCard = ({
  integration,
  isPending,
  isConnected,
  isSkipped,
  onConnected,
  onSkip,
}: IntegrationCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showConnectModal = useConnectModal();
  const template = integration.template;

  const isConnectedFromQuery = template?.connectionStatus === 'connected';

  useEffect(() => {
    if (isConnectedFromQuery && !isConnected) {
      onConnected(integration.templateShortId);
    }
  }, [isConnectedFromQuery, isConnected, onConnected, integration.templateShortId]);

  const handleConnect = useCallback(() => {
    if (!template || !integration.hasAuthentications) return;

    const authentication = template.authentications[0];
    if (!authentication) return;

    const props: ConnectModalProps = {
      template,
      authentication,
      journeyActionId: integration.actionShortId,
      onSuccess: () => {
        onConnected(integration.templateShortId);
      },
    };
    showConnectModal(props);
  }, [template, integration, onConnected, showConnectModal]);

  const isCardConnected = isConnected || isConnectedFromQuery;
  const isEffectivelySkipped = isSkipped || (!isPending && !isCardConnected);

  return (
    <Stack orientation="horizontal" space={3} align="center">
      <IntegrationIcon src={integration.iconUrl} size="sm" />
      <CardContent>
        <Text font="semibold14" text={integration.name} color="textPrimary" ellipsisWithTooltip />
      </CardContent>
      {isCardConnected ? (
        <Stack orientation="horizontal" space={1} align="center" style={{ flexShrink: 0 }}>
          <IconCheckmark color="elementGreen100" size={14} />
          <Text font="semibold12" text={t('connected')} color="elementGreen100" />
        </Stack>
      ) : isEffectivelySkipped ? (
        <Text font="regular12" text={t('skipped')} color="textHelper" />
      ) : (
        <Stack orientation="horizontal" space={2} align="center">
          <Button
            variant="secondary"
            size="sm"
            text={t('skip')}
            onClick={() => onSkip(integration.templateShortId)}
            isDisabled={!isPending}
          />
          <Button
            variant="primary-blue"
            size="sm"
            text={t('connect')}
            onClick={handleConnect}
            isDisabled={!isPending || !integration.hasAuthentications || !template}
          />
        </Stack>
      )}
    </Stack>
  );
};

const Container = styled(Stack).attrs({ space: 2 })({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  background: theme.color.orangeTrans10,
});

const CardContent = styled.div({
  flex: '1 1 0',
  minWidth: 0,
});

const Footer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'flex-end',
  space: 2,
})({});

const locale = {
  en: {
    titleSingle: 'Connect {name} to continue',
    titleMultiple: 'Connect to continue',
    connect: 'Connect',
    skip: 'Skip',
    skipAll: 'Skip all',
    connected: 'Connected',
    skipped: 'Skipped',
  },
};
